import { useCallback, useEffect, useId, useState } from 'react';
import { Button } from '../../../components/Button';
import { SelectPopover } from '../../../components/SelectPopover';
import { TextInput } from '../../../components/TextInput';
import { ApiError } from '../../../services/api';
import { getBillingPlan, saveBillingPlan } from '../../../services/admin-billing';
import type {
  BillingInterval,
  BillingPlan,
  BillingPlanStatus,
} from '../../../types/adminBilling';
import {
  INTERVAL_LABEL,
  PAYMENT_METHOD_KIND_LABEL,
  PLAN_STATUS_LABEL,
  centsToDollarsInput,
  dateOnlyCell,
  dollarsToCents,
  formatMoney,
} from './billingFormat';
import { AdminPanel } from '../AdminPage';

const INTERVALS: readonly BillingInterval[] = ['Monthly', 'Annual'];
const STATUSES: readonly BillingPlanStatus[] = ['Active', 'Paused', 'Cancelled'];
interface PlanForm {
  amount: string;
  currency: string;
  interval: BillingInterval;
  paymentTermsDays: string;
  firstInvoiceOn: string;
  status: BillingPlanStatus;
  notes: string;
}

const EMPTY_FORM: PlanForm = {
  amount: '',
  currency: 'USD',
  interval: 'Monthly',
  paymentTermsDays: '30',
  firstInvoiceOn: '',
  status: 'Active',
  notes: '',
};

/** The stored plan, back into the form. Cents become a plain dollars string, never a float. */
const formFromPlan = (plan: BillingPlan): PlanForm => ({
  amount: centsToDollarsInput(plan.amountCents),
  currency: plan.currency,
  interval: plan.interval,
  paymentTermsDays: String(plan.paymentTermsDays),
  firstInvoiceOn: plan.firstInvoiceOn,
  status: plan.status,
  notes: plan.notes,
});

interface BillingPlanPanelProps {
  organizationId: string;
  /** Called after a successful save, so the page's summary picks up the new next invoice date. */
  onSaved: () => void;
}

/**
 * The organization's plan: what it is charged, how often, and from when.
 *
 * **Money is typed in dollars and sent in cents**, converted by `dollarsToCents` with string
 * arithmetic — see that function for why `value * 100` is not good enough.
 *
 * **`nextInvoiceOn` is shown but never sent.** The API derives it from the interval and what it
 * has already invoiced; a client that echoed back the value it happened to be holding would pin
 * the schedule to whatever was on screen when the page loaded.
 */
export const BillingPlanPanel = ({ organizationId, onSaved }: BillingPlanPanelProps) => {
  const [plan, setPlan] = useState<BillingPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<PlanForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [amountError, setAmountError] = useState<string | null>(null);

  const amountId = useId();
  const intervalId = useId();
  const statusId = useId();
  const methodId = useId();
  const notesId = useId();

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      setLoadError(null);
      return getBillingPlan(organizationId, signal)
        .then((loaded) => {
          if (signal?.aborted) return;
          setPlan(loaded);
          setLoading(false);
        })
        .catch((err: unknown) => {
          if (signal?.aborted) return;
          // A 404 here is "no plan yet", which is an ordinary state for a new organization and not
          // something to put an error banner on.
          if (err instanceof ApiError && err.statusCode === 404) {
            setPlan(null);
            setLoading(false);
            return;
          }
          setLoadError(
            err instanceof ApiError && err.message ? err.message : 'Could not load the plan.'
          );
          setLoading(false);
        });
    },
    [organizationId]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const startEditing = () => {
    setForm(plan ? formFromPlan(plan) : EMPTY_FORM);
    setFormError(null);
    setAmountError(null);
    setEditing(true);
  };

  const update = <K extends keyof PlanForm>(key: K, value: PlanForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving) return;

    setFormError(null);
    setAmountError(null);

    const amountCents = dollarsToCents(form.amount);
    if (amountCents === null) {
      setAmountError('Enter an amount in dollars, for example 1234.56.');
      return;
    }

    const paymentTermsDays = Number(form.paymentTermsDays);
    if (!Number.isInteger(paymentTermsDays) || paymentTermsDays < 0) {
      setFormError('Payment terms must be a whole number of days.');
      return;
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.firstInvoiceOn)) {
      setFormError('Pick the date of the first invoice.');
      return;
    }

    setSaving(true);
    try {
      const saved = await saveBillingPlan(organizationId, {
        amountCents,
        currency: form.currency.trim().toUpperCase() || 'USD',
        interval: form.interval,
        paymentTermsDays,
        // Sent exactly as the date input holds it, which is already "yyyy-MM-dd". Nothing here
        // goes through a Date, so nothing can shift by the admin's UTC offset.
        firstInvoiceOn: form.firstInvoiceOn,
        status: form.status,
        notes: form.notes,
      });
      if (saved) setPlan(saved);
      else await load();
      setEditing(false);
      onSaved();
    } catch (err: unknown) {
      setFormError(
        err instanceof ApiError && err.message ? err.message : 'Could not save the plan.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminPanel
      title="Plan"
      titleId="billing-plan-title"
      actions={
        !editing &&
        !loading && (
          <Button variant="secondary" onClick={startEditing} data-testid="billing-plan-edit">
            {plan ? 'Edit plan' : 'Add a plan'}
          </Button>
        )
      }
    >

      {loading && <p className="admin-state">Loading the plan...</p>}

      {loadError && (
        <p className="admin-error" role="alert">
          {loadError}
        </p>
      )}

      {!loading && !editing && !plan && (
        <p className="billing-panel__note" data-testid="billing-plan-empty">
          This organization has no plan. Nothing will be invoiced and no reminder will go out until
          one is added.
        </p>
      )}

      {!loading && !editing && plan && (
        <dl className="billing-panel__facts" data-testid="billing-plan-facts">
          <div className="billing-panel__fact">
            <dt className="billing-panel__fact-label">Amount</dt>
            <dd className="billing-panel__fact-value" data-testid="billing-plan-amount">
              {formatMoney(plan.amountCents, plan.currency)} {INTERVAL_LABEL[plan.interval].toLowerCase()}
            </dd>
          </div>
          <div className="billing-panel__fact">
            <dt className="billing-panel__fact-label">Status</dt>
            <dd className="billing-panel__fact-value">{PLAN_STATUS_LABEL[plan.status]}</dd>
          </div>
          <div className="billing-panel__fact">
            <dt className="billing-panel__fact-label">Payment terms</dt>
            <dd className="billing-panel__fact-value">{plan.paymentTermsDays} days</dd>
          </div>
          <div className="billing-panel__fact">
            <dt className="billing-panel__fact-label">First invoice</dt>
            <dd className="billing-panel__fact-value">{dateOnlyCell(plan.firstInvoiceOn)}</dd>
          </div>
          <div className="billing-panel__fact">
            <dt className="billing-panel__fact-label">Next invoice</dt>
            <dd className="billing-panel__fact-value" data-testid="billing-plan-next">
              {dateOnlyCell(plan.nextInvoiceOn)}
            </dd>
          </div>
          <div className="billing-panel__fact">
            <dt className="billing-panel__fact-label">Default payment method</dt>
            <dd className="billing-panel__fact-value">
              {PAYMENT_METHOD_KIND_LABEL[plan.defaultPaymentMethodKind]}
              {plan.defaultPaymentMethodLabel ? ` (${plan.defaultPaymentMethodLabel})` : ''}
            </dd>
          </div>
          {plan.notes && (
            <div className="billing-panel__fact billing-panel__field--wide">
              <dt className="billing-panel__fact-label">Notes</dt>
              <dd className="billing-panel__fact-value">{plan.notes}</dd>
            </div>
          )}
        </dl>
      )}

      {editing && (
        <form className="billing-form" onSubmit={handleSubmit} data-testid="billing-plan-form">
          <div className="billing-panel__form">
            <TextInput
              id={amountId}
              label="Amount (dollars)"
              inputMode="decimal"
              placeholder="1234.56"
              value={form.amount}
              onChange={(e) => update('amount', e.target.value)}
              error={amountError ?? undefined}
              disabled={saving}
              data-testid="billing-plan-amount-input"
            />
            <TextInput
              label="Currency"
              value={form.currency}
              onChange={(e) => update('currency', e.target.value)}
              disabled={saving}
              data-testid="billing-plan-currency-input"
            />
            <div>
              <label className="billing-panel__label" htmlFor={intervalId}>
                Interval
              </label>
              <SelectPopover
                id={intervalId}
                options={INTERVALS}
                value={form.interval}
                onChange={(value) => update('interval', value as BillingInterval)}
                placeholder="How often"
                clearable={false}
                disabled={saving}
                fullWidth
                testId="billing-plan-interval"
              />
            </div>
            <TextInput
              label="Payment terms (days)"
              inputMode="numeric"
              value={form.paymentTermsDays}
              onChange={(e) => update('paymentTermsDays', e.target.value)}
              disabled={saving}
              data-testid="billing-plan-terms-input"
            />
            <TextInput
              label="First invoice on"
              type="date"
              value={form.firstInvoiceOn}
              onChange={(e) => update('firstInvoiceOn', e.target.value)}
              disabled={saving}
              data-testid="billing-plan-first-invoice-input"
            />
            <div>
              <label className="billing-panel__label" htmlFor={statusId}>
                Status
              </label>
              <SelectPopover
                id={statusId}
                options={STATUSES}
                value={form.status}
                onChange={(value) => update('status', value as BillingPlanStatus)}
                placeholder="Plan status"
                clearable={false}
                disabled={saving}
                fullWidth
                testId="billing-plan-status"
              />
            </div>
            {/*
              Shown, not edited. Stripe owns these two: the webhook writes them when a customer puts
              a method on file and clears them when one is removed. They were editable controls whose
              values the API discarded, so saving appeared to work and then reverted.
            */}
            {plan && (
              /*
                A dl, not a span plus a p with aria-labelledby: `paragraph` is a role that prohibits
                an author-supplied name, so the pairing is dropped and a screen reader reads the
                label and the value as unrelated neighbouring text. This is the same dt/dd shape the
                non-editing facts list in this file already uses.
              */
              <dl className="billing-panel__facts">
                <div className="billing-panel__fact">
                  <dt className="billing-panel__fact-label" id={methodId}>
                    Default payment method
                  </dt>
                  <dd className="billing-panel__fact-value">
                    {PAYMENT_METHOD_KIND_LABEL[plan.defaultPaymentMethodKind]}
                    {plan.defaultPaymentMethodLabel ? `, ${plan.defaultPaymentMethodLabel}` : ''}
                    {plan.defaultPaymentMethodKind === 'None'
                      ? '. Set one from the Stripe panel below.'
                      : ''}
                  </dd>
                </div>
              </dl>
            )}
            <div className="billing-panel__field--wide">
              <label className="billing-panel__label" htmlFor={notesId}>
                Notes
              </label>
              <textarea
                id={notesId}
                className="billing-panel__textarea"
                value={form.notes}
                onChange={(e) => update('notes', e.target.value)}
                disabled={saving}
                data-testid="billing-plan-notes-input"
              />
            </div>
          </div>

          <p className="billing-form__hint">
            The next invoice date is worked out by the server from the interval and what has already
            been invoiced, so it is not editable here.
          </p>

          {formError && (
            <p className="billing-form__error" role="alert" data-testid="billing-plan-error">
              {formError}
            </p>
          )}

          <div className="billing-panel__actions">
            <Button type="submit" loading={saving} data-testid="billing-plan-save">
              {saving ? 'Saving...' : 'Save plan'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setEditing(false)}
              disabled={saving}
              data-testid="billing-plan-cancel"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </AdminPanel>
  );
};
